import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { ArrowLeft, ArrowLeftRight, ChevronLeft, ChevronRight, ChevronDown, Lock, CheckCircle2, Circle, AlertCircle, MessageCircle, Eye, Footprints, Dumbbell, Bike, Mountain, Trophy, Flag, Zap, PersonStanding, Activity, Moon, X, Plus, Trash2 } from "lucide-react";
import AddCustomWorkoutModal from "../../components/athlete/AddCustomWorkoutModal";
import { athleteGetProgram, athleteShiftWorkout, getCoachAthletePreview, athleteMe, athleteGetLactate, athleteListStepLogs, athleteUpsertStepLog, athleteDeleteCustomWorkout } from "../../lib/api";
import { useAthleteAuth } from "../../lib/athleteAuth";
import { computeWeekActuals, computeProgramActuals, formatVolume, isWeekPast, logsByKey as buildLogsByKey, weekDateRange, formatDateRange, formatDateShort, WORKOUT_TYPE_MAP } from "../../lib/coaching";
import { RichTextView, looksLikeHtml } from "../../components/RichTextEditor";
import { linkify } from "../../lib/linkify";
import { computeVdot, trainingPaces, lactateTrainingPaces } from "../../lib/vdot";
import AthleteWorkoutLogModal from "../../components/athlete/AthleteWorkoutLogModal";
import AthleteNotificationBell from "../../components/athlete/AthleteNotificationBell";
import WorkoutDetail from "../../components/athlete/WorkoutDetail";
import AthleteNav from "../../components/athlete/AthleteNav";
import StravaMatchChooser from "../../components/athlete/StravaMatchChooser";
import LockedProgramCard from "../../components/athlete/LockedProgramCard";
import { toast } from "sonner";

// Default Mon-first week. Used when the program doesn't override via
// `start_day`. The `dayLabelsForProgram` helper below rotates this list so the
// day labels rendered next to each cell match the actual calendar weekday of
// the date stored at that index (e.g. for a Sunday-start program, index 0 is
// "Sun", index 1 is "Mon", etc.). Without this, athletes saw "Wed" next to a
// Tuesday date — exactly the bug Sheri reported.
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const DAY_LABELS_SUN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_LABELS_NUMERIC = ["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"];

function dayLabelsForProgram(program) {
  const sd = (program?.start_day || "mon").toLowerCase();
  if (sd === "sun") return DAY_LABELS_SUN;
  if (sd === "day1") return DAY_LABELS_NUMERIC;
  return DAY_LABELS;
}

function StatusIcon({ status }) {
  if (status === "complete") return <CheckCircle2 size={14} className="text-emerald-700" />;
  if (status === "partial") return <AlertCircle size={14} className="text-amber-600" />;
  if (status === "skipped") return <Circle size={14} className="text-[var(--tir-burgundy)]" />;
  return null;
}

function fmtMin(mins) {
  if (!mins) return "0m";
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function PlannedActualCard({ label, planned, actual, pct }) {
  const clamped = Math.max(0, Math.min(1.5, pct || 0));
  // Bar colour: green if 0.9–1.1, amber if 0.7–0.9 or 1.1–1.3, burgundy otherwise.
  let barColor = "#7A1F2B";
  if (clamped >= 0.9 && clamped <= 1.1) barColor = "#2F855A";
  else if ((clamped >= 0.7 && clamped < 0.9) || (clamped > 1.1 && clamped <= 1.3)) barColor = "#D69E2E";
  return (
    <div className="flex-1 flex flex-col items-center justify-center bg-white/60 p-5 border-t sm:border-t-0 sm:border-l first:border-t-0 first:sm:border-l-0 border-[var(--tir-border)]">
      <div className="text-[10px] tracking-[0.22em] uppercase font-mono-tir text-[var(--tir-muted)]">{label}</div>
      <div className="font-serif-display text-5xl text-[var(--tir-ink)] mt-2 leading-none tracking-tight">{actual}</div>
      <div className="text-[10px] text-[var(--tir-muted)] mt-2">/ {planned}</div>
      <div className="mt-3 h-1.5 bg-[var(--tir-border)] rounded-full w-full max-w-[120px] overflow-hidden">
        <div
          style={{ width: `${Math.min(100, clamped * 100)}%`, background: barColor }}
          className="h-full transition-all"
        />
      </div>
    </div>
  );
}

export default function AthleteProgramPage({ coachPreview = false } = {}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // In coach-preview mode we skip the athlete-auth hook and treat the coach
  // (already authed by the parent route) as the viewer. The page becomes
  // read-only — workout-log buttons are hidden.
  const athleteAuth = useAthleteAuth();
  const athlete = coachPreview ? { id: "coach-preview" } : athleteAuth.athlete;
  const loading = coachPreview ? false : athleteAuth.loading;
  const [program, setProgram] = useState(null);
  const [activeWeekIdx, setActiveWeekIdx] = useState(0);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [introModal, setIntroModal] = useState(null); // null | "howto" | "modify"
  const [logsByKey, setLogsByKey] = useState({}); // key = `${week}-${day}-${woId}`
  // Athlete profile + latest lactate test — used to derive per-athlete
  // training paces so the km-from-minutes estimation in weekly mileage
  // totals matches THIS athlete's actual easy/tempo paces (rather than a
  // generic 6:00/km default). Both fields are optional — if either fetch
  // fails we fall through to defaults.
  const [athleteProfile, setAthleteProfile] = useState(null);
  const [athleteLactate, setAthleteLactate] = useState(null);
  // Athlete's daily step-count logs, keyed by ISO date (yyyy-mm-dd). Only
  // populated on athlete portal (not coach preview); refreshed on load
  // and whenever a chip is tapped. Used to colour the day-cell step chip
  // green (complete), red (missed), or the default un-logged pill.
  const [stepLogsByDate, setStepLogsByDate] = useState({});
  // Deep-link (?week=N&workout=ID) from the notification bell / push /
  // email — applied once on first load so replies land in the SAME
  // thread the coach wrote on, instead of whichever workout the athlete
  // opens by hand.
  const deepLinkHandledRef = useRef(false);

  useEffect(() => {
    if (coachPreview) {
      if (id) refresh();
      return;
    }
    if (loading) return;
    if (!athleteAuth.athlete) {
      const next = encodeURIComponent(location.pathname + location.search);
      navigate(`/athlete/login?next=${next}`, { replace: true });
      return;
    }
    if (id) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [athleteAuth.athlete, id, loading, coachPreview]);

  const refresh = async () => {
    try {
      const p = coachPreview
        ? await getCoachAthletePreview(id)
        : await athleteGetProgram(id);
      setProgram(p);
      setLogsByKey(buildLogsByKey(p.workout_logs || []));
      // Fan-out: load athlete profile (pace_profile) + latest lactate test
      // in parallel. Best-effort — failure is non-fatal, paces just fall
      // back to per-type defaults inside workoutVolume(). Skipped in
      // coach-preview mode (the coach side already has its own pace pipe).
      if (!coachPreview) {
        Promise.allSettled([athleteMe(), athleteGetLactate()]).then(([meRes, lacRes]) => {
          if (meRes.status === "fulfilled") setAthleteProfile(meRes.value);
          if (lacRes.status === "fulfilled") setAthleteLactate(lacRes.value);
        });
        // Fetch step logs for the whole program window so every day cell
        // can render its chip in the correct colour without an extra
        // round-trip per week switch.
        refreshStepLogs(p);
      }
      // Land on the athlete's CURRENT week — the latest unlocked week whose
      // start date is on or before today. Falls back to the first unlocked
      // week (e.g. if the program hasn't started yet, week 1).
      const weeks = p.weeks || [];
      const startIso = p.start_date;
      let landIdx = 0;
      if (startIso && weeks.length) {
        try {
          const start = new Date(startIso + "T00:00:00");
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          let currentIdx = -1;
          for (let i = 0; i < weeks.length; i++) {
            const wn = Number(weeks[i].week_number || i + 1);
            const ws = new Date(start.getTime());
            ws.setDate(ws.getDate() + (wn - 1) * 7);
            if (ws <= today) currentIdx = i;
          }
          if (currentIdx >= 0 && !weeks[currentIdx].locked) {
            landIdx = currentIdx;
          } else {
            // Program hasn't started yet — land on the first unlocked week.
            const firstUnlocked = weeks.findIndex((w) => !w.locked);
            landIdx = firstUnlocked >= 0 ? firstUnlocked : 0;
          }
        } catch {
          /* keep landIdx = 0 */
        }
      }
      setActiveWeekIdx(applyDeepLink(weeks, landIdx));
    } catch (err) {
      const status = err?.response?.status;
      if (coachPreview) {
        if (status === 404) {
          toast.error("Program not found");
          navigate(-1);
        } else {
          toast.error("Couldn't load program preview");
        }
        return;
      }
      if (status === 404) {
        toast.error("That program isn't on your portal — it may have been removed.");
        navigate("/athlete/home", { replace: true });
      } else if (status === 401) {
        const next = encodeURIComponent(location.pathname + location.search);
        navigate(`/athlete/login?next=${next}`, { replace: true });
      } else {
        toast.error("Couldn't load program");
      }
    }
  };

  // Resolve ?week=N and ?workout=ID once: switch to that week (if
  // unlocked) and auto-open that workout's log modal so the athlete
  // replies on the exact thread the coach used.
  const applyDeepLink = (weeks, landIdx) => {
    if (deepLinkHandledRef.current || coachPreview) return landIdx;
    deepLinkHandledRef.current = true;
    const params = new URLSearchParams(location.search);
    const wkParam = parseInt(params.get("week") || "", 10);
    const woParam = params.get("workout");
    let idx = landIdx;
    if (!Number.isNaN(wkParam)) {
      const wIdx = weeks.findIndex(
        (w) => Number(w.week_number) === wkParam && !w.locked
      );
      if (wIdx >= 0) idx = wIdx;
    }
    if (woParam && weeks[idx] && !weeks[idx].locked) {
      for (const d of weeks[idx].days || []) {
        const wo = (d.workouts || []).find((x) => x.id === woParam);
        if (wo) {
          setEditingWorkout({ workout: wo, dayLabel: d.label });
          break;
        }
      }
    }
    return idx;
  };

  const refreshStepLogs = async (p) => {
    // Skip in coach preview — the coach doesn't log steps for an athlete.
    // Range: program start → program end + a bit of buffer. Guards
    // against a wide-open query if the program has no start_date.
    if (coachPreview) return;
    const prog = p || program;
    if (!prog?.start_date || !(prog.weeks || []).length) return;
    try {
      const start = new Date(prog.start_date + "T00:00:00");
      const end = new Date(start.getTime());
      end.setDate(end.getDate() + prog.weeks.length * 7);
      const iso = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      const logs = await athleteListStepLogs(iso(start), iso(end));
      const byDate = {};
      for (const lg of logs || []) {
        if (lg?.date) byDate[lg.date] = lg;
      }
      setStepLogsByDate(byDate);
    } catch {
      // Non-fatal — the chip just falls back to un-logged state.
    }
  };

  // Per-athlete training paces. Prefers real lactate data → falls back to
  // VDOT-derived Daniels paces from the athlete's pace profile → null
  // (which triggers the per-type defaults inside workoutVolume).
  const athletePaces = useMemo(() => {
    const lacPaces = lactateTrainingPaces(athleteLactate);
    if (lacPaces) return lacPaces;
    const pp = athleteProfile?.pace_profile;
    if (!pp) return null;
    const vdot = computeVdot(pp.race_distance_m, pp.race_time_seconds);
    return trainingPaces(vdot);
  }, [athleteLactate, athleteProfile]);

  // (Program-wide stats roll-up removed — coach + athlete UX requested
  // per-week numbers only. computeProgramActuals is still imported in case
  // we want to surface them elsewhere later, but we don't compute them on
  // every render anymore.)

  const weekStats = useMemo(() => {
    if (!program) return null;
    const w = program.weeks?.[activeWeekIdx];
    if (!w || w.locked) return null;
    return computeWeekActuals(w, logsByKey, { isPast: isWeekPast(program, w), athletePaces });
  }, [program, logsByKey, activeWeekIdx, athletePaces]);

  if (loading || !athlete || !program) {
    return <div className="min-h-screen flex items-center justify-center text-[var(--tir-muted)]" data-testid="athlete-program-loading">Loading…</div>;
  }

  // Payment gate: If the athlete's coaching payment isn't active AND we are
  // NOT in coach-preview mode, replace the entire program view with a locked
  // card. Coach-preview always shows the real program (so the coach can still
  // review programming for an unpaid athlete). Backend also strips the
  // workouts payload when locked so devtools can't peek at the plan.
  if (!coachPreview && program.paid_active === false) {
    return (
      <div className="min-h-screen bg-[var(--tir-ivory)]" data-testid="athlete-program-page-locked">
        <header className="border-b border-[var(--tir-border)] bg-white">
          <div className="max-w-5xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between gap-4">
            <Link to="/athlete/home" className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-navy)] inline-flex items-center gap-1">
              <ArrowLeft size={11} /> All programs
            </Link>
            <div className="flex items-center gap-2 min-w-0">
              <div className="text-right min-w-0">
                <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-burgundy)] font-semibold truncate">
                  {program.target_race || "Training"}
                </div>
                <div className="font-serif-display text-xl text-[var(--tir-ink)] truncate">{program.title}</div>
              </div>
              <AthleteNotificationBell />
            </div>
          </div>
        </header>
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8">
          <LockedProgramCard paymentLink={program.payment_link} programTitle={program.title} />
        </div>
      </div>
    );
  }

  const week = program.weeks[activeWeekIdx];
  const onLogged = (log) => {
    const k = `${log.week_number}-${log.day_label}-${log.workout_id}`;
    setLogsByKey((prev) => ({ ...prev, [k]: log }));
    setEditingWorkout(null);
  };

  return (
    <div className="min-h-screen bg-[var(--tir-ivory)]" data-testid={coachPreview ? "coach-preview-page" : "athlete-program-page"}>
      {coachPreview && (
        <div className="bg-[var(--tir-navy)] text-white text-center py-2 px-4 text-[10px] tracking-[0.22em] uppercase font-semibold flex items-center justify-center gap-2" data-testid="coach-preview-banner">
          <Eye size={13} /> Coach preview ·{" "}
          {program._preview_athlete_name || "athlete"} sees{" "}
          {program._preview_drip_weeks || 4} week
          {(program._preview_drip_weeks || 4) === 1 ? "" : "s"} ahead
        </div>
      )}
      <header className="border-b border-[var(--tir-border)] bg-white">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-4 flex items-center justify-between gap-4">
          {coachPreview ? (
            <button
              type="button"
              onClick={() => navigate(-1)}
              data-testid="coach-preview-back"
              className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-navy)] inline-flex items-center gap-1"
            >
              <ArrowLeft size={11} /> Back to athlete
            </button>
          ) : (
            <Link to="/athlete/home" className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-navy)] inline-flex items-center gap-1">
              <ArrowLeft size={11} /> All programs
            </Link>
          )}
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-right min-w-0">
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-burgundy)] font-semibold truncate">
                {program.target_race || "Training"}
              </div>
              <div className="font-serif-display text-xl text-[var(--tir-ink)] truncate">{program.title}</div>
            </div>
            {!coachPreview && <AthleteNotificationBell />}
          </div>
        </div>
      </header>
      {!coachPreview && <AthleteNav />}

      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-6 pb-24 md:pb-8 space-y-6">
        {/* Strava double-day disambiguation. Self-hides when there's nothing
            pending, so it costs nothing on a normal week. Hidden in coach
            preview mode (the coach can't act on the athlete's behalf here). */}
        {!coachPreview && (
          <StravaMatchChooser onResolved={refresh} />
        )}
        {/* Week stats: Planned vs Actual for the active week only (program-wide
            roll-up removed at coach request — coaches/athletes want each
            week's numbers in isolation, not a running cumulative). */}
        {weekStats && weekStats.planned.count > 0 && (
          <section data-testid="athlete-week-stats">
            <div className="flex items-baseline justify-between mb-2">
              <h2 className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-muted)]">
                Week {program.weeks?.[activeWeekIdx]?.week_number} · this week's progress
              </h2>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)]">
                {weekStats.actual.complete + weekStats.actual.partial} / {weekStats.planned.count} workouts ·{" "}
                <span className="text-[var(--tir-ink)] font-semibold">
                  {Math.round((weekStats.actual.completion_pct || 0) * 100)}%
                </span>
              </div>
            </div>
            <div className="bg-[var(--tir-ivory)] rounded-lg border border-[var(--tir-border)] flex flex-col sm:flex-row shadow-sm overflow-hidden">
              {(weekStats.planned.km > 0.05 || weekStats.actual.km > 0.05) && (
                <PlannedActualCard
                  label="Distance"
                  planned={`${weekStats.planned.km.toFixed(weekStats.planned.km >= 10 ? 0 : 1)} km`}
                  actual={`${weekStats.actual.km.toFixed(weekStats.actual.km >= 10 ? 0 : 1)} km`}
                  pct={weekStats.planned.km > 0 ? weekStats.actual.km / weekStats.planned.km : 0}
                />
              )}
              {(weekStats.planned.minutes > 0.5 || weekStats.actual.minutes > 0.5) && (
                <PlannedActualCard
                  label="Time"
                  planned={fmtMin(weekStats.planned.minutes)}
                  actual={fmtMin(weekStats.actual.minutes)}
                  pct={weekStats.planned.minutes > 0 ? weekStats.actual.minutes / weekStats.planned.minutes : 0}
                />
              )}
              <div className="flex-1 flex flex-col items-center justify-center bg-white/60 p-5 border-t sm:border-t-0 sm:border-l border-[var(--tir-border)]">
                <div className="text-[10px] tracking-[0.22em] uppercase font-mono-tir text-[var(--tir-muted)]">Complete</div>
                <div className="font-serif-display text-5xl text-emerald-700 mt-2 leading-none tracking-tight" data-testid="stat-complete">
                  {weekStats.actual.complete}
                </div>
                <div className="text-[10px] text-[var(--tir-muted)] mt-2 text-center">
                  {weekStats.actual.partial} partial · of {weekStats.planned.count}
                </div>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center bg-white/60 p-5 border-t sm:border-t-0 sm:border-l border-[var(--tir-border)]">
                <div className="text-[10px] tracking-[0.22em] uppercase font-mono-tir text-[var(--tir-muted)]">Missed</div>
                <div className="font-serif-display text-5xl text-[var(--tir-burgundy)] mt-2 leading-none tracking-tight" data-testid="stat-missed">
                  {weekStats.actual.missed + weekStats.actual.skipped}
                </div>
                <div className="text-[10px] text-[var(--tir-muted)] mt-2 text-center">
                  {weekStats.actual.skipped} marked · {weekStats.actual.missed} unlogged
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Week strip */}
        <section className="flex items-center gap-2">
          <button
            onClick={() => setActiveWeekIdx((i) => Math.max(0, i - 1))}
            disabled={activeWeekIdx === 0}
            className="h-9 w-9 border border-[var(--tir-border)] bg-white disabled:opacity-30 flex items-center justify-center"
            aria-label="Prev week"
          >
            <ChevronLeft size={15} />
          </button>
          <div className="flex-1 min-w-0 overflow-x-auto">
            <div className="flex gap-1.5 pb-1">
              {program.weeks.map((w, i) => {
                const active = i === activeWeekIdx;
                const locked = w.locked;
                let cls = "bg-white border-[var(--tir-border)] text-[var(--tir-ink)] hover:border-[var(--tir-ink)]";
                if (active && !locked) cls = "bg-[var(--tir-navy)] text-white border-[var(--tir-navy)]";
                if (locked) cls = "bg-[#f3eee5] border-[var(--tir-border)] text-[var(--tir-muted)] opacity-60 cursor-not-allowed";
                // Show the week-of date (e.g. "Jun 8") as the primary
                // pill label instead of "W1/W2/…", per coach request.
                // Falls back to "W<n>" if the program has no start_date.
                const [ws] = weekDateRange(program.start_date, w.week_number);
                const pillLabel = ws ? formatDateShort(ws) : `W${w.week_number}`;
                return (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => !locked && setActiveWeekIdx(i)}
                    disabled={locked}
                    data-testid={`athlete-week-tab-${w.week_number}`}
                    title={`Week ${w.week_number}${ws ? ` · starts ${formatDateShort(ws)}` : ""}`}
                    className={`px-3 h-9 text-xs tracking-[0.18em] uppercase font-semibold border inline-flex items-center gap-1.5 shrink-0 whitespace-nowrap ${cls}`}
                  >
                    <span>{pillLabel}</span>
                    {locked && <Lock size={10} />}
                  </button>
                );
              })}
            </div>
          </div>
          <button
            onClick={() => setActiveWeekIdx((i) => Math.min(program.weeks.length - 1, i + 1))}
            disabled={activeWeekIdx >= program.weeks.length - 1 || program.weeks[activeWeekIdx + 1]?.locked}
            className="h-9 w-9 border border-[var(--tir-border)] bg-white disabled:opacity-30 flex items-center justify-center"
            aria-label="Next week"
          >
            <ChevronRight size={15} />
          </button>
        </section>

        {/* Day grid */}
        {week?.locked ? (
          <div className="border border-dashed border-[var(--tir-border)] py-16 text-center bg-white">
            <Lock size={20} className="mx-auto text-[var(--tir-muted)] mb-2" />
            <div className="font-serif-display text-xl">Week {week.week_number} is locked</div>
            <p className="text-xs text-[var(--tir-muted)] mt-2 max-w-md mx-auto">
              Future weeks unlock as you progress through the program. Stay focused on this week's work.
            </p>
          </div>
        ) : (
          <>
            {/* Big "Week of …" heading — anchors the whole page so the
                athlete can see at a glance which calendar week this plan is for. */}
            {(() => {
              const [ws, we] = weekDateRange(program.start_date, week.week_number);
              if (!ws || !we) return null;
              return (
                <div
                  className="border-b border-[var(--tir-ink)] pb-3"
                  data-testid="athlete-week-of-heading"
                >
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[var(--tir-burgundy)] font-semibold mb-1">
                    Week {week.week_number}
                    {week.phase_label ? <span className="text-[var(--tir-muted)] font-normal"> · {week.phase_label}</span> : null}
                  </div>
                  <h2 className="font-serif-display text-2xl sm:text-3xl text-[var(--tir-ink)] leading-tight">
                    Week of {formatDateRange(ws, we)}
                  </h2>
                  {(program.intro_howto || program.intro_modify) && (
                    <div
                      className="flex flex-wrap items-center gap-2 mt-3"
                      data-testid="athlete-intro-buttons"
                    >
                      {program.intro_howto && (
                        <button
                          type="button"
                          onClick={() => setIntroModal("howto")}
                          data-testid="athlete-intro-howto-btn"
                          className="px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold bg-[var(--tir-navy)] text-white rounded-sm hover:bg-[#152a45] transition-colors"
                        >
                          How to Use This Program
                        </button>
                      )}
                      {program.intro_modify && (
                        <button
                          type="button"
                          onClick={() => setIntroModal("modify")}
                          data-testid="athlete-intro-modify-btn"
                          className="px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold bg-[var(--tir-navy)] text-white rounded-sm hover:bg-[#152a45] transition-colors"
                        >
                          How to Modify
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
            {/* Week-level coach note (if any). Always visible right under the
                "Week of …" heading so the athlete sees the coach's intent for
                the week before scanning individual days. */}
            {week.coach_note && (
              <div
                className="bg-[#FAF7F2] border-l-4 border-[var(--tir-burgundy)] px-4 py-3"
                data-testid="athlete-week-coach-note"
              >
                <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-burgundy)] font-semibold mb-1 inline-flex items-center gap-1">
                  <MessageCircle size={10} /> Coach's note for the week
                </div>
                <div className="text-[12.5px] leading-relaxed text-[var(--tir-ink)] whitespace-pre-wrap">
                  {week.coach_note}
                </div>
              </div>
            )}
            {/* Warm-up + Cool-down quick links. Rendered by default (the
                field is undefined on legacy weeks → treat as enabled) so
                the athlete has one-tap access to the boilerplate mobility
                flow at the start and end of every run. Coach can uncheck
                per-week when they've programmed a custom warm-up. */}
            {week.show_warmup_cooldown_links !== false && (
              <div
                className="flex flex-wrap items-center gap-2"
                data-testid="athlete-warmup-cooldown-links"
              >
                <a
                  href="https://the-intuitive-runner.fitterapp.app/workouts/warm-up"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="athlete-warmup-link"
                  className="inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-burgundy)] border border-[var(--tir-burgundy)] rounded-sm hover:bg-[var(--tir-burgundy)] hover:text-white transition-colors"
                >
                  <span aria-hidden="true">↗</span> Warm-up routine
                </a>
                <a
                  href="https://the-intuitive-runner.fitterapp.app/workouts/stretch"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="athlete-cooldown-link"
                  className="inline-flex items-center gap-2 px-3 py-2 text-[11px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-navy)] border border-[var(--tir-navy)] rounded-sm hover:bg-[var(--tir-navy)] hover:text-white transition-colors"
                >
                  <span aria-hidden="true">↗</span> Cool-down / stretch
                </a>
                <span className="text-[10px] italic text-[var(--tir-muted)]">
                  Do these before + after every workout
                </span>
              </div>
            )}
            {/* Per-week summary strip — now reduced to JUST the % complete
                badge. The 4 tiles above already show distance / time /
                complete / missed for this week; the strip was redundant
                noise that crowded the planner. % stays because it's the
                one quick "am I on track" signal that's not already
                surfaced above as a single number. */}
            {weekStats && weekStats.planned.count > 0 && (
              <div
                className="flex items-center justify-end px-1"
                data-testid="athlete-week-summary"
              >
                <span className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
                  <span className="text-[var(--tir-ink)] font-bold">
                    {Math.round((weekStats.actual.completion_pct || 0) * 100)}%
                  </span>{" "}
                  complete
                </span>
              </div>
            )}
            <div
              className="flex flex-col gap-2"
              data-testid="athlete-week-canvas"
            >
            {(() => {
              const labels = dayLabelsForProgram(program);
              // Use LOCAL date (not UTC) so athletes in tz-west-of-UTC
              // don't see tomorrow's date marked "Today" after 8 PM. The
              // previous `new Date().toISOString().slice(0,10)` was UTC
              // — e.g. June 9 9 PM EDT → "2026-06-10" UTC, so the WED
              // tile would (incorrectly) show TODAY on a Thursday.
              const localIso = (d) =>
                `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
              const todayIso = localIso(new Date());
              const races = program.races || [];
              return (week?.days || []).map((day, di) => {
              // Compute the calendar date for this weekday so the athlete sees
              // e.g. "MON · May 18" in the day header.
              let dayDate = null;
              let dayIso = null;
              if (program?.start_date && week?.week_number) {
                try {
                  const d = new Date(program.start_date + "T00:00:00");
                  d.setDate(d.getDate() + (Number(week.week_number) - 1) * 7 + di);
                  if (!isNaN(d.getTime())) {
                    dayDate = d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                    dayIso = localIso(d);
                  }
                } catch { /* ignore */ }
              }
              const dayLabel = labels[di] || `Day ${di + 1}`;
              const isToday = dayIso === todayIso;
              // Inject any scheduled races that fall on this date as a
              // synthetic "race" workout, so they render as a tile in
              // the day row AND can be logged (RPE/notes/status) via the
              // same modal as regular workouts. We use `race:<id>` as the
              // workout_id so it never collides with real workouts and
              // can be reliably round-tripped through workout_logs.
              const raceEntries = races.filter((r) => r?.date === dayIso);
              const raceWorkouts = raceEntries.map((r) => ({
                id: `race:${r.id}`,
                type: "race",
                title: r.name || "Race",
                duration_or_distance: r.distance || "",
                is_race: true,
                race_priority: r.priority || null,
                // Surface race notes through `session_note` so the modal's
                // "Workout details" panel already renders them for free.
                session_note: r.notes || "",
              }));
              const mergedWorkouts = [...(day.workouts || []), ...raceWorkouts];
              const dayWithRaces = mergedWorkouts.length === (day.workouts || []).length
                ? day
                : { ...day, workouts: mergedWorkouts };
              const allRest = mergedWorkouts.length > 0 && mergedWorkouts.every((w) => w.type === "rest");
              const hasNoWorkouts = mergedWorkouts.length === 0;
              return (
              <DayRow
                key={day.id}
                day={dayWithRaces}
                dayLabel={dayLabel}
                dayDate={dayDate}
                dayIso={dayIso}
                dayIndex={di}
                isToday={isToday}
                isRestDay={hasNoWorkouts || allRest}
                week={week}
                logsByKey={logsByKey}
                coachPreview={coachPreview}
                programId={program.id}
                weekDayLabels={labels}
                onShifted={refresh}
                stepLog={dayIso ? stepLogsByDate[dayIso] : null}
                onStepLogSaved={(saved) => {
                  if (saved && saved.deleted && dayIso) {
                    setStepLogsByDate((prev) => {
                      const next = { ...prev };
                      delete next[dayIso];
                      return next;
                    });
                  } else if (saved && saved.date) {
                    setStepLogsByDate((prev) => ({ ...prev, [saved.date]: saved }));
                  }
                }}
                onPickWorkout={(wo) =>
                  setEditingWorkout({ workout: wo, dayLabel: day.label })
                }
              />
              );
            });
            })()}
            </div>
          </>
        )}
      </div>

      {introModal && (
        <IntroNoteModal
          title={introModal === "howto" ? "How to Use This Program" : "How to Modify"}
          content={introModal === "howto" ? program.intro_howto : program.intro_modify}
          onClose={() => setIntroModal(null)}
        />
      )}

      {editingWorkout && (
        <AthleteWorkoutLogModal
          workout={editingWorkout.workout}
          programId={program.id}
          weekNumber={week.week_number}
          dayLabel={editingWorkout.dayLabel}
          existingLog={logsByKey[`${week.week_number}-${editingWorkout.dayLabel}-${editingWorkout.workout.id}`]}
          onClose={() => setEditingWorkout(null)}
          onSaved={onLogged}
        />
      )}
    </div>
  );
}

// Resolve a workout type to its lucide icon component. Falls back to
// Activity for anything unmapped. Keeps the per-type icon mapping in
// /app/frontend/src/lib/coaching.js as a string so it can also be
// referenced from the coach editor without dragging React into the data
// module.
const ICON_BY_NAME = {
  Footprints, Dumbbell, Bike, Mountain, Trophy, Flag, Zap, PersonStanding, Activity, Moon,
};

// Full-screen modal showing the program's intro notes ("How to use this
// program" / "How to modify") — same content that opens the printed PDF.
function IntroNoteModal({ title, content, onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
      data-testid="athlete-intro-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl bg-white border border-[var(--tir-ink)] shadow-2xl max-h-[88vh] overflow-y-auto rounded-t-lg sm:rounded-none">
        <div className="flex items-start justify-between p-4 sm:p-6 border-b border-[var(--tir-border)] sticky top-0 bg-white z-10">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold">
              Program guide
            </div>
            <h3 className="font-serif-display text-xl sm:text-2xl text-[var(--tir-ink)] leading-tight">
              {title}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)] p-1"
            aria-label="Close"
            data-testid="athlete-intro-modal-close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-4 sm:p-6">
          {looksLikeHtml(content) ? (
            <RichTextView html={content} className="text-sm text-[var(--tir-ink)] leading-relaxed" />
          ) : (
            <div className="text-sm text-[var(--tir-ink)] leading-relaxed whitespace-pre-wrap">
              {linkify(content)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getWorkoutIcon(type) {
  const meta = WORKOUT_TYPE_MAP[type];
  return ICON_BY_NAME[meta?.icon || "Activity"] || Activity;
}
function getWorkoutAccent(type) {
  return WORKOUT_TYPE_MAP[type]?.accent || "#6F6A66";
}

/**
 * DayRow — flagship redesigned single-line-per-day layout.
 *
 * Each day is a single OUTER BOX (white card with day-pill on the left)
 * containing 1..N collapsed workout tiles inside. Tapping a tile expands
 * it inline to show the intervals/notes, plus a "Log this workout →"
 * button which opens the full log modal. This keeps the page calm: title
 * + icon by default, full detail only on demand.
 *
 * Per-workout accent colour follows the WORKOUT_TYPES.accent palette
 * (recovery=blue, easy=green, tempo/race=yellow, threshold/speed=red,
 * strength=purple, cross=brown, etc.).
 *
 * Status pill bar (left edge of each tile):
 *     complete = green   #2F855A on #F0FDF4 bg
 *     partial  = amber   #D69E2E on #FEFCE8 bg
 *     missed   = burgundy #7A1F2B on #FDF2F2 bg
 *     today (unlogged) = subtle navy ring + "TODAY" chip
 *     future / unlogged = neutral hairline (type-accent shown as a thin stripe)
 *     rest day = quiet dashed treatment, no inner workouts
 */
function DayRow({ day, dayLabel, dayDate, dayIso, dayIndex, isToday, isRestDay, week, logsByKey, coachPreview, stepLog, onStepLogSaved, onPickWorkout, programId, weekDayLabels, onShifted }) {
  const [showAddCustom, setShowAddCustom] = useState(false);
  const workouts = day.workouts || [];
  // Per-workout expand state, keyed by workout id. Two workouts in the
  // same day can each be expanded independently.
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  // Outer day-box border: subtle by default, navy when "today".
  const boxBorder = isToday ? "border-[var(--tir-navy)] border-2" : "border-[var(--tir-border)]";

  // Daily-step-goal chip — only rendered when the coach turned the goal
  // on for this week AND this specific day index isn't in the excluded
  // list (race day, etc.). Chip lives inside the day-box so it feels
  // attached to the day rather than floating between workouts.
  const stepGoal = week?.step_goal;
  const stepGoalActive =
    !!stepGoal?.enabled &&
    !(stepGoal.excluded_days || []).includes(dayIndex);
  // Effective target for THIS specific day: per-day override wins over
  // the week default. Empty/missing override falls back to the week's
  // `target` field. day_targets is keyed by string ("0".."6") since it
  // round-trips through JSON.
  const effectiveStepTarget = (() => {
    if (!stepGoalActive) return null;
    const overrides = stepGoal.day_targets || {};
    const override = overrides[String(dayIndex)];
    if (Number.isFinite(override) && override > 0) return override;
    return Number(stepGoal.target) || 10000;
  })();

  return (
    <div data-testid={`athlete-day-row-${dayLabel}`}>
      {day.coach_note && (
        <div
          className="bg-[#FAF7F2] border-l-4 border-[var(--tir-burgundy)] px-4 py-2.5 rounded-r-md mb-2"
          data-testid={`athlete-day-${dayLabel}-note`}
        >
          <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-burgundy)] font-semibold mb-1 inline-flex items-center gap-1">
            <MessageCircle size={10} /> {dayLabel} · Coach
          </div>
          {looksLikeHtml(day.coach_note) ? (
            <RichTextView
              html={day.coach_note}
              className="text-[12px] leading-snug text-[var(--tir-ink)]"
            />
          ) : (
            <div className="text-[12px] leading-snug text-[var(--tir-ink)] whitespace-pre-wrap">
              {day.coach_note}
            </div>
          )}
        </div>
      )}
      <div
        className={`flex items-stretch bg-white rounded-md overflow-hidden border ${boxBorder} ${
          isRestDay ? "border-dashed bg-white/50" : ""
        }`}
      >
        {/* Day-pill (shared across all workouts in this day) */}
        <div className="w-16 md:w-20 shrink-0 flex flex-col items-center justify-center bg-black/[0.03] border-r border-[var(--tir-border)] py-3">
          <div className="font-serif-display text-xl md:text-2xl uppercase text-[var(--tir-ink)] leading-none tracking-wide">
            {dayLabel}
          </div>
          {dayDate && (
            <div className="text-[10px] font-mono-tir text-[var(--tir-muted)] mt-1">{dayDate}</div>
          )}
          {isToday && (
            <div className="text-[8px] tracking-[0.22em] uppercase font-bold text-[var(--tir-navy)] mt-1.5">
              Today
            </div>
          )}
        </div>
        {/* Right side — list of workout tiles, divided by a hairline so
            two workouts on the same day clearly belong together. */}
        <div className="flex-1 flex flex-col min-w-0 divide-y divide-[var(--tir-border)]">
          {isRestDay ? (
            <div className="flex-1 flex items-center px-4 py-4">
              <span className="text-[12px] italic text-[var(--tir-muted)]">Rest</span>
            </div>
          ) : (
            workouts.map((wo, woIdx) => {
              const log = logsByKey[`${week.week_number}-${day.label}-${wo.id}`];
              const isRest = wo.type === "rest";
              const status = log?.status;
              const statusStyles = {
                complete: { bar: "#2F855A", bg: "#F0FDF4" },
                partial: { bar: "#D69E2E", bg: "#FEFCE8" },
                skipped: { bar: "#7A1F2B", bg: "#FDF2F2" },
              };
              const palette = statusStyles[status] || null;
              const interactive = !isRest && !coachPreview;
              const accent = getWorkoutAccent(wo.type);
              const Icon = getWorkoutIcon(wo.type);
              const isExpanded = !!expanded[wo.id];
              const typeLabel = WORKOUT_TYPE_MAP[wo.type]?.label || wo.type;
              return (
                <React.Fragment key={wo.id}>
                  {woIdx > 0 && wo.is_alternative && (
                    <div
                      className="flex items-center gap-2 px-3 py-0.5 bg-white"
                      data-testid={`athlete-day-${day.label}-or-divider`}
                    >
                      <div className="flex-1 h-px bg-[var(--tir-border)]" />
                      <span className="text-[9px] tracking-[0.28em] uppercase font-bold text-[var(--tir-burgundy)]">
                        OR
                      </span>
                      <div className="flex-1 h-px bg-[var(--tir-border)]" />
                    </div>
                  )}
                  <div className="flex items-stretch relative" data-testid={`athlete-workout-${wo.id}`}>
                    {/* Status bar (logged status takes priority); otherwise a
                        thin type-accent stripe so the workout family is
                        still recognisable at a glance. */}
                    <div
                      className="w-2 shrink-0"
                      style={{ backgroundColor: palette ? palette.bar : accent }}
                      aria-hidden="true"
                    />
                    {/* Header row — always visible */}
                    <button
                      type="button"
                      onClick={() => interactive && toggle(wo.id)}
                      disabled={!interactive}
                      data-testid={`athlete-workout-toggle-${wo.id}`}
                      aria-expanded={isExpanded}
                      className={`flex-1 flex items-center gap-3 px-4 py-3 text-left min-w-0 ${
                        interactive ? "cursor-pointer hover:bg-black/[0.02]" : "cursor-default"
                      }`}
                      style={palette ? { backgroundColor: palette.bg } : undefined}
                    >
                      <div
                        className="w-9 h-9 shrink-0 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${accent}15`, color: accent }}
                        aria-hidden="true"
                      >
                        <Icon size={18} strokeWidth={2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {wo.is_alternative && (
                            <span className="text-[var(--tir-burgundy)]">↳</span>
                          )}
                          {wo.is_optional && (
                            <span
                              className="inline-block text-[8px] tracking-[0.2em] uppercase font-bold text-[var(--tir-navy)] border border-[var(--tir-navy)]/40 px-1.5 rounded-sm"
                              data-testid={`athlete-optional-badge-${wo.id}`}
                            >
                              Optional
                            </span>
                          )}
                          {wo.is_race && (
                            <span
                              className="inline-block text-[8px] tracking-[0.2em] uppercase font-bold text-white bg-[var(--tir-burgundy)] px-1.5 py-0.5 rounded-sm"
                              data-testid={`athlete-race-badge-${wo.id}`}
                            >
                              {wo.race_priority ? `Race · ${wo.race_priority}` : "Race"}
                            </span>
                          )}
                          {wo.added_by_athlete && (
                            <span
                              className="inline-block text-[8px] tracking-[0.2em] uppercase font-bold text-white bg-[var(--tir-navy)] px-1.5 py-0.5 rounded-sm"
                              data-testid={`athlete-added-badge-${wo.id}`}
                            >
                              Added by you
                            </span>
                          )}
                          <span className="text-[14px] md:text-[15px] font-semibold text-[var(--tir-ink)] leading-snug truncate">
                            {wo.title || typeLabel}
                          </span>
                        </div>
                        {/* Single-line metadata: distance/duration + type
                            label. Detailed intervals only on expand. */}
                        {(wo.duration_or_distance || (!wo.title && typeLabel)) && (
                          <div className="text-[11px] font-mono-tir text-[var(--tir-muted)] tracking-wide mt-0.5 truncate">
                            {wo.duration_or_distance ? wo.duration_or_distance : ""}
                            {wo.duration_or_distance && wo.title ? " · " : ""}
                            {wo.title ? typeLabel : ""}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {isToday && !status && (
                          <span className="text-[8px] tracking-[0.22em] uppercase font-bold text-white bg-[var(--tir-navy)] px-1.5 py-0.5 rounded-sm">
                            Today
                          </span>
                        )}
                        {log && <StatusIcon status={log.status} />}
                        <ChevronDown
                          size={16}
                          className="text-[var(--tir-muted)] transition-transform"
                          style={{ transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}
                        />
                      </div>
                    </button>
                  </div>
                  {/* Expanded panel — only mounts when toggled. Houses the
                      interval breakdown, athlete note, Strava badge, and a
                      "Log this workout →" CTA that opens the full modal. */}
                  {isExpanded && (
                    <div
                      className="px-4 py-3 bg-[var(--tir-ivory)] text-[12px] space-y-2 ml-2"
                      data-testid={`athlete-workout-expanded-${wo.id}`}
                    >
                      {wo.shifted_from_day && (
                        <div
                          className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-navy)] font-semibold"
                          data-testid={`athlete-workout-shifted-${wo.id}`}
                        >
                          Moved from {wo.shifted_from_day}
                        </div>
                      )}
                      <WorkoutDetail workout={wo} />
                      {log?.source === "strava_auto" && (
                        <div
                          className="text-[9px] tracking-[0.18em] uppercase text-[#fc4c02] font-semibold"
                          data-testid={`athlete-strava-auto-badge-${wo.id}`}
                          title="Auto-matched from Strava — tap to edit if it's not quite right"
                        >
                          ✦ Auto-matched from Strava
                        </div>
                      )}
                      {log?.athlete_note && (
                        <div className="text-[11px] text-[var(--tir-muted)] italic border-t border-dashed border-[var(--tir-border)] pt-1.5">
                          "{log.athlete_note}"
                        </div>
                      )}
                      {interactive && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onPickWorkout(wo);
                          }}
                          data-testid={`athlete-workout-log-${wo.id}`}
                          className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] mt-1"
                        >
                          {log ? "Edit log" : "Log this workout"}
                          <ChevronRight size={14} />
                        </button>
                      )}
                      {interactive && wo.added_by_athlete && (
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              await athleteDeleteCustomWorkout(wo.id);
                              toast.success("Workout removed");
                              onShifted?.();
                            } catch {
                              toast.error("Couldn't remove — try again");
                            }
                          }}
                          data-testid={`athlete-delete-custom-${wo.id}`}
                          className="ml-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] mt-1"
                        >
                          <Trash2 size={12} /> Remove
                        </button>
                      )}
                      {/* Move-to-another-day picker. Only shown to the athlete
                          (not coach-preview, not on rest workouts, and only
                          when we have the plumbing wired). Hidden on race
                          workouts — races are date-fixed. */}
                      {interactive && programId && weekDayLabels && onShifted && wo.type !== "rest" && !wo.is_race && (
                        <ShiftMenu
                          workout={wo}
                          currentDayLabel={dayLabel}
                          weekDayLabels={weekDayLabels}
                          programId={programId}
                          weekNumber={week.week_number}
                          onShifted={onShifted}
                        />
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })
          )}
          {!coachPreview && programId && (
            <button
              type="button"
              onClick={() => setShowAddCustom(true)}
              data-testid={`athlete-add-workout-${day.label}`}
              className="flex items-center gap-1.5 px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-muted)]/70 hover:text-[var(--tir-navy)] transition-colors"
            >
              <Plus size={12} /> Add your own
            </button>
          )}
        </div>
      </div>
      {showAddCustom && (
        <AddCustomWorkoutModal
          programId={programId}
          weekNumber={week.week_number}
          dayLabel={day.label}
          dayName={dayLabel}
          onClose={() => setShowAddCustom(false)}
          onAdded={() => {
            setShowAddCustom(false);
            onShifted?.();
          }}
        />
      )}
      {/* Daily step-goal chip — small pill at the bottom of every day
          cell when the coach enabled the goal for this week and this
          day isn't excluded. Athlete taps to expand a lightweight
          inline logger (actual steps + complete/missed). */}
      {stepGoalActive && (
        <StepChip
          dayIso={dayIso}
          dayLabel={dayLabel}
          stepGoal={stepGoal}
          effectiveTarget={effectiveStepTarget}
          log={stepLog}
          readOnly={coachPreview}
          onSaved={onStepLogSaved}
        />
      )}
    </div>
  );
}


/**
 * StepChip — single day's daily-step-goal pill. Renders at the bottom
 * of a DayRow when the coach turned the week's step goal on and didn't
 * exclude this weekday.
 *
 * States (drives colour):
 *   - un-logged: burgundy outline pill
 *   - complete: solid green
 *   - missed:   solid burgundy
 * Tap expands an inline form with a steps-count number input and
 * complete / missed / clear buttons.
 */
function StepChip({ dayIso, dayLabel, stepGoal, effectiveTarget, log, readOnly, onSaved }) {
  const [open, setOpen] = useState(false);
  const [actual, setActual] = useState(
    log?.actual_steps != null ? String(log.actual_steps) : "",
  );
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    setActual(log?.actual_steps != null ? String(log.actual_steps) : "");
  }, [log?.actual_steps]);

  // `target` for this specific day — driven by the per-day override if
  // set, else the week's default. Fed in from DayRow so both the chip
  // and the log-upsert body use the same number.
  const target = Number(effectiveTarget ?? stepGoal?.target ?? 10000);
  const status = log?.status || null;
  const isComplete = status === "complete";
  const isMissed = status === "missed";

  // Colour recipe mirrors workout status pills for continuity.
  let pillClasses = "border border-[var(--tir-burgundy)] text-[var(--tir-burgundy)] bg-white";
  if (isComplete) pillClasses = "border border-[#2F855A] text-white bg-[#2F855A]";
  else if (isMissed) pillClasses = "border border-[#7A1F2B] text-white bg-[#7A1F2B]";

  const chipLabel = (() => {
    const actualNum = log?.actual_steps;
    const actualTxt = actualNum != null ? actualNum.toLocaleString() : null;
    if (isComplete) {
      return actualTxt
        ? `✓ ${actualTxt} steps`
        : `✓ Step goal hit`;
    }
    if (isMissed) {
      return actualTxt
        ? `✗ Missed · ${actualTxt} steps`
        : `✗ Missed step goal`;
    }
    if (actualTxt) return `${actualTxt} / ${target.toLocaleString()} steps`;
    return `Daily goal · ${target.toLocaleString()} steps`;
  })();

  const save = async (nextStatus) => {
    if (readOnly || !dayIso) return;
    setSaving(true);
    try {
      const parsed = actual === "" ? null : parseInt(actual, 10);
      const body = {
        date: dayIso,
        status: nextStatus,
        actual_steps: Number.isFinite(parsed) ? parsed : null,
        target,
      };
      const saved = await athleteUpsertStepLog(body);
      onSaved && onSaved(saved && saved.deleted ? { deleted: true, date: dayIso } : saved);
      setOpen(false);
    } catch (err) {
      toast.error("Couldn't save step log");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (readOnly || !dayIso) return;
    setSaving(true);
    try {
      const saved = await athleteUpsertStepLog({
        date: dayIso,
        status: null,
        actual_steps: null,
      });
      onSaved && onSaved(saved && saved.deleted ? { deleted: true, date: dayIso } : saved);
      setActual("");
      setOpen(false);
    } catch {
      toast.error("Couldn't clear step log");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-1.5 flex flex-col" data-testid={`step-chip-${dayIso || dayLabel}`}>
      <button
        type="button"
        onClick={() => !readOnly && setOpen((v) => !v)}
        disabled={readOnly}
        data-testid={`step-chip-toggle-${dayIso || dayLabel}`}
        aria-expanded={open}
        className={
          "self-start inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] tracking-[0.08em] uppercase font-semibold transition-colors " +
          pillClasses +
          (readOnly ? " cursor-default" : " hover:opacity-90 cursor-pointer")
        }
      >
        <Footprints size={12} />
        <span>{chipLabel}</span>
      </button>
      {open && (
        <div
          className="mt-1.5 self-start w-full max-w-sm border border-[var(--tir-border)] rounded-md bg-white p-3 shadow-sm"
          data-testid={`step-chip-panel-${dayIso || dayLabel}`}
        >
          <div className="text-[9px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1.5">
            Steps for {dayLabel}
            {target > 0 && (
              <span className="ml-1 normal-case tracking-normal font-normal italic text-[var(--tir-muted)]/80">
                — goal {target.toLocaleString()}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              inputMode="numeric"
              min={0}
              step={100}
              value={actual}
              onChange={(e) => setActual(e.target.value)}
              placeholder="Actual steps"
              data-testid={`step-chip-actual-input-${dayIso || dayLabel}`}
              className="flex-1 min-w-0 bg-transparent border-0 border-b border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] py-1 font-mono-tir text-base focus:outline-none"
            />
            <span className="text-[10px] tracking-[0.14em] uppercase text-[var(--tir-muted)] font-semibold">
              steps
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => save("complete")}
              disabled={saving}
              data-testid={`step-chip-complete-${dayIso || dayLabel}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase font-semibold border border-[#2F855A] text-[#2F855A] hover:bg-[#2F855A] hover:text-white rounded-sm transition-colors disabled:opacity-50"
            >
              <CheckCircle2 size={12} /> Complete
            </button>
            <button
              type="button"
              onClick={() => save("missed")}
              disabled={saving}
              data-testid={`step-chip-missed-${dayIso || dayLabel}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-[0.18em] uppercase font-semibold border border-[#7A1F2B] text-[#7A1F2B] hover:bg-[#7A1F2B] hover:text-white rounded-sm transition-colors disabled:opacity-50"
            >
              <Circle size={12} /> Missed
            </button>
            {status && (
              <button
                type="button"
                onClick={clear}
                disabled={saving}
                data-testid={`step-chip-clear-${dayIso || dayLabel}`}
                className="ml-auto text-[10px] tracking-[0.14em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-ink)] px-2 disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}



/**
 * ShiftMenu — inline "Move workout to another day" picker for the athlete.
 * Renders a compact button strip of the current week's day labels. Tapping
 * a different day POSTs the shift and calls `onShifted()` so the parent
 * refetches the program (which then shows the workout in its new day with a
 * "Moved from X" marker). Tapping the CURRENT day undoes the shift.
 */
function ShiftMenu({ workout, currentDayLabel, weekDayLabels, programId, weekNumber, onShifted }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const originalDay = workout.shifted_from_day || currentDayLabel;

  const move = async (toDay) => {
    if (busy) return;
    setBusy(true);
    try {
      await athleteShiftWorkout(programId, {
        week_number: weekNumber,
        workout_id: workout.id,
        to_day: toDay,
      });
      // Success toast reflects what happened from the athlete's mental model.
      if (toDay === originalDay) {
        toast.success(`Moved back to ${toDay}`);
      } else {
        toast.success(`Moved to ${toDay}. Your coach has been notified.`);
      }
      setOpen(false);
      onShifted && onShifted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't move workout.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        data-testid={`athlete-workout-move-open-${workout.id}`}
        className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)] mt-2"
        title="Move this workout to a different day in the same week"
      >
        <ArrowLeftRight size={12} /> Move to another day
      </button>
    );
  }

  return (
    <div
      className="mt-2 space-y-1"
      onClick={(e) => e.stopPropagation()}
      data-testid={`athlete-workout-move-panel-${workout.id}`}
    >
      <div className="text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] font-semibold">
        Move to (same week only)
      </div>
      <div className="flex flex-wrap gap-1.5">
        {weekDayLabels.map((lbl) => {
          const isCurrent = lbl === currentDayLabel;
          const isOriginal = lbl === originalDay && workout.shifted_from_day;
          return (
            <button
              key={lbl}
              type="button"
              onClick={() => move(lbl)}
              disabled={busy || isCurrent}
              data-testid={`athlete-workout-move-to-${workout.id}-${lbl}`}
              className={`px-2.5 py-1 text-[10px] tracking-[0.18em] uppercase font-semibold border transition-colors ${
                isCurrent
                  ? "text-white bg-[var(--tir-navy)] border-[var(--tir-navy)] cursor-default"
                  : isOriginal
                  ? "text-[var(--tir-burgundy)] border-[var(--tir-burgundy)]/50 hover:bg-[var(--tir-burgundy)] hover:text-white"
                  : "text-[var(--tir-ink)] border-[var(--tir-border)] hover:border-[var(--tir-navy)]"
              } disabled:opacity-60`}
            >
              {lbl}
              {isOriginal && !isCurrent ? " · undo" : ""}
            </button>
          );
        })}
        <button
          type="button"
          onClick={() => setOpen(false)}
          disabled={busy}
          data-testid={`athlete-workout-move-cancel-${workout.id}`}
          className="ml-auto px-2 text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
